import React, { useState, useEffect, useRef } from "react";
import { Modal } from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import "../styles/PackModal.css";

// Utility function to get product image with correct priority
const getProductImage = (product) => {
  const defaultImage =
    "https://developmentprocureasy.s3.ap-south-1.amazonaws.com/swageazyproducts/Swageazy-Swagpack-Without-Background_1699332397642.png";

  // 1st Priority: Mockup images with priority handling
  if (product?.mockup_images?.length > 0) {
    // Check for priority 1 mockups first
    const priority1Mockup = product.mockup_images.find(
      (img) => img.priority === 1,
    );
    if (priority1Mockup) {
      return priority1Mockup.image_url;
    }
    // If no priority 1, use first mockup (priority 0)
    return product.mockup_images[0].image_url;
  }

  // 2nd Priority: Product images
  if (product?.images?.length > 0) {
    return product.images[0].image_url;
  }

  // 3rd Priority: Default
  return defaultImage;
};

// Build a gallery array: prefer mockups (priority 1 first), then product images, then fallback
const buildProductGallery = (product) => {
  const fallback =
    "https://developmentprocureasy.s3.ap-south-1.amazonaws.com/swageazyproducts/Swageazy-Swagpack-Without-Background_1699332397642.png";

  let urls = [];
  if (product?.mockup_images?.length > 0) {
    const sorted = [...product.mockup_images].sort(
      (a, b) => Number(b?.priority || 0) - Number(a?.priority || 0),
    );
    urls = sorted.map((m) => m.image_url).filter(Boolean);
  }
  if (urls.length === 0 && product?.images?.length > 0) {
    urls = product.images.map((i) => i.image_url).filter(Boolean);
  }
  if (urls.length === 0) {
    urls = [fallback];
  }
  return urls.map((u) => ({ original: u, thumbnail: u }));
};

const PackModal = ({
  open,
  onClose,
  packDetails,
  preSelectedProduct,
  customerId,
}) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const descriptionRef = useRef(null);

  // Reset selection when modal opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedProduct(null);
      setShowFullDescription(false);
    } else if (preSelectedProduct) {
      // If modal opens with a pre-selected product, set it directly
      setSelectedProduct(preSelectedProduct);
      setShowFullDescription(false);
    } else if (packDetails?.products?.length > 0) {
      // If no pre-selected product but modal opens, auto-select the first product
      // This ensures we always go directly to detailed view, skipping grid view
      setSelectedProduct({ ...packDetails.products[0], originalIndex: 0 });
      setShowFullDescription(false);
    }
  }, [open, preSelectedProduct, packDetails]);

  // Check if description needs read more
  useEffect(() => {
    if (selectedProduct && descriptionRef.current && !showFullDescription) {
      const description = descriptionRef.current;
      const container = description.parentElement;

      // Show "Read more" only when the clamped text is actually cut off
      const shouldShowReadMore =
        description.scrollHeight > description.clientHeight + 1;

      if (shouldShowReadMore) {
        container.classList.add("has-overflow");
      } else {
        container.classList.remove("has-overflow");
      }
    }
  }, [selectedProduct, showFullDescription]);

  const handleProductClick = (product, index) => {
    setSelectedProduct({ ...product, originalIndex: index });
    setShowFullDescription(false);
  };

  const toggleDescription = () => {
    setShowFullDescription(!showFullDescription);
  };

  const galleryItems = selectedProduct
    ? buildProductGallery(selectedProduct)
    : [];
  const productCount = packDetails?.products?.length || 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      center
      classNames={{
        modal: selectedProduct
          ? "packProductsModal detailed-view"
          : "packProductsModal",
        modalAnimationIn: "customEnterModalAnimation",
        modalAnimationOut: "customLeaveModalAnimation",
      }}
      animationDuration={400}
    >
      <div className="row">
        <div className="col-12 pack-modal-header">
          <h2 className="pack-heading">Products in your pack</h2>
          <div className="pack-subheading d-flex flex-column">
            {customerId &&
            Number(customerId) === Number(process.env.REACT_APP_CUSTOMER_ID) ? (
              <span>
                We're delighted to share with you the contents of your pack
              </span>
            ) : (
              <>
                <span>
                  We're delighted to share with you the contents of your special
                  Swag pack.
                </span>
                <span>
                  Below is the list of products included. We hope you'll find
                  joy and satisfaction in each item.
                </span>
              </>
            )}
          </div>
        </div>

        {packDetails?.description && (
          <div className="col-12">
            <div className="pack-modal-description">
              <span className="desc">About this pack</span>
              <span>{packDetails?.description}</span>
            </div>
          </div>
        )}
        <div className="col-12 mt-3">
          {selectedProduct ? (
            // Detailed view layout
            <div className="detailed-layout">
              <div className="selected-product-section">
                <div className="large-image-container">
                  <ImageGallery
                    items={galleryItems}
                    showPlayButton={false}
                    showFullscreenButton={false}
                    showNav={galleryItems.length > 1}
                    showBullets={galleryItems.length > 1}
                    showThumbnails={false}
                    lazyLoad={true}
                  />
                </div>
                <div className="selected-product-info">
                  <h3 className="selected-product-name">
                    {selectedProduct?.product_name || "--"}
                  </h3>
                  <div className="description-container">
                    <div
                      ref={descriptionRef}
                      className={`selected-product-description ${
                        showFullDescription ? "expanded" : ""
                      }`}
                      dangerouslySetInnerHTML={{
                        __html:
                          selectedProduct?.product_description ||
                          "Product description not available.",
                      }}
                    />
                    <button
                      type="button"
                      className="read-more-link"
                      onClick={toggleDescription}
                    >
                      {showFullDescription ? "Show less" : "Read more"}
                      <i
                        className={`bi ${
                          showFullDescription
                            ? "bi-chevron-up"
                            : "bi-chevron-down"
                        }`}
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </div>
              </div>

              <div className="products-sidebar">
                <p className="products-sidebar-title">
                  In this pack
                  <span className="products-sidebar-count">
                    {productCount}
                  </span>
                </p>
                <div className="sidebar-products-list">
                  {packDetails?.products?.map((product, index) => (
                    <button
                      type="button"
                      key={index}
                      className={`sidebar-product-item ${
                        selectedProduct?.originalIndex === index ? "active" : ""
                      }`}
                      aria-pressed={selectedProduct?.originalIndex === index}
                      onClick={() => handleProductClick(product, index)}
                    >
                      <div className="sidebar-product-card">
                        <div className="sidebar-img-div">
                          <img
                            src={getProductImage(product)}
                            className="sidebar-mockimg"
                            alt={product?.product_name}
                            onError={(e) => {
                              e.target.src =
                                "https://developmentprocureasy.s3.ap-south-1.amazonaws.com/swageazyproducts/Swageazy-Swagpack-Without-Background_1699332397642.png";
                            }}
                          />
                        </div>
                      </div>
                      <span
                        className="sidebar-product-name"
                        title={product?.product_name}
                      >
                        {product?.product_name || "--"}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            // Grid view layout
            <div className="products-grid">
              {packDetails?.products?.map((product, index) => (
                <div
                  key={index}
                  className="product-item"
                  onClick={() => handleProductClick(product, index)}
                >
                  <div className="product-card">
                    <div className="img-div">
                      <img
                        src={getProductImage(product)}
                        className="mockimg"
                        alt={`Product ${index}`}
                        onError={(e) => {
                          e.target.src =
                            "https://developmentprocureasy.s3.ap-south-1.amazonaws.com/swageazyproducts/Swageazy-Swagpack-Without-Background_1699332397642.png";
                        }}
                      />
                    </div>
                  </div>
                  <p className="product-name">
                    {product?.product_name || "--"}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default PackModal;
